import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState, memo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useUnresolvedErrorCount } from "@/hooks/useUnresolvedErrorCount";
import { useHomeData } from "@/hooks/useHomeData";
import { toast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, BookOpen, LayoutDashboard, Settings, Users, Lock, BarChart3, Activity, Menu, Info, FileText, MessageSquare, MoreHorizontal, ChevronDown, AlertTriangle, Target, Zap, ExternalLink, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { LogoBrand } from "./LogoBrand";
import { NotificationDropdown } from "./NotificationDropdown";
import { BacklogButton } from "./BacklogButton";

interface MainNavProps {
  showLogo?: boolean;
}

export const MainNav = memo(function MainNav({ showLogo = true }: MainNavProps) {
  const { user, signOut } = useAuth();
  const { isAdmin } = useUserRole();
  const { count: errorCount } = useUnresolvedErrorCount();
  const { data: homeData } = useHomeData();
  const [mobileAdminOpen, setMobileAdminOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [mobileManageContentsOpen, setMobileManageContentsOpen] = useState(false);

  const adminLockedMsg = user ? "Admin only" : "Sign in to access admin";
  const progressLockedMsg = user ? "" : "This feature requires log in";

  return (
    <header className="sticky top-0 z-40 border-b bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      <div className="container px-5 sm:px-6 flex items-center justify-between py-3 sm:py-4">
        {showLogo ? (
          <Link to="/" className="flex items-center">
            <LogoBrand size="sm" />
          </Link>
        ) : (
          <div />
        )}
        {/* Mobile: single menu button to avoid header overflow/scroll */}
        <div className="flex items-center gap-2 sm:hidden">
          {/* Mobile backlog button (admin only) */}
          <BacklogButton />
          {/* Mobile notification button */}
          <NotificationDropdown 
            quizLearnSets={homeData?.quizLearnSets}
            courses={homeData?.courses}
          />
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                className="px-2.5 shadow-sm ring-1 ring-border"
                aria-label="Open menu"
                data-track-cta
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw]">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>

              <div className="mt-6 grid gap-2">
                {/* Core navigation */}
                <SheetClose asChild>
                  <Link to="/browse" className="w-full">
                    <Button variant="secondary" className="w-full justify-start gap-2" data-track-cta>
                      <BookOpen className="h-4 w-4" /> Learn
                    </Button>
                  </Link>
                </SheetClose>

                <SheetClose asChild>
                  <Link to="/quiz-learn" className="w-full">
                    <Button variant="secondary" className="w-full justify-start gap-2" data-track-cta>
                      <Target className="h-4 w-4" /> Quiz & Learn
                    </Button>
                  </Link>
                </SheetClose>

                {user ? (
                  <SheetClose asChild>
                    <Link to="/dashboard" className="w-full">
                      <Button variant="secondary" className="w-full justify-start gap-2" data-track-cta>
                        <LayoutDashboard className="h-4 w-4" /> Progress
                      </Button>
                    </Link>
                  </SheetClose>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full justify-start gap-2"
                    data-track-cta
                    onClick={() => {
                      toast({
                        title: "Login required",
                        description: "This feature requires log in.",
                      });
                    }}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span className="flex items-center gap-2">
                      Progress <Lock className="h-4 w-4 opacity-70" />
                    </span>
                  </Button>
                )}

                <SheetClose asChild>
                  <Link to="/about" className="w-full">
                    <Button variant="secondary" className="w-full justify-start gap-2" data-track-cta>
                      <Info className="h-4 w-4" /> About
                    </Button>
                  </Link>
                </SheetClose>

                {/* More section: Analytics, Blog, Contact */}
                <Collapsible open={mobileMoreOpen} onOpenChange={setMobileMoreOpen}>
                  <CollapsibleTrigger asChild>
                    <Button type="button" variant="secondary" className="w-full justify-between">
                      <span className="flex items-center gap-2">
                        <MoreHorizontal className="h-4 w-4" /> More
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileMoreOpen ? "rotate-180" : ""}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 ml-4 grid gap-1">
                    <SheetClose asChild>
                      <Link to="/products" className="w-full">
                        <Button variant="ghost" className="w-full justify-start gap-2" data-track-cta>
                          <Sparkles className="h-4 w-4" /> Products
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/analytics" className="w-full">
                        <Button variant="ghost" className="w-full justify-start gap-2" data-track-cta>
                          <BarChart3 className="h-4 w-4" /> Analytics
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/blog" className="w-full">
                        <Button variant="ghost" className="w-full justify-start gap-2" data-track-cta>
                          <FileText className="h-4 w-4" /> Blog
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/contact" className="w-full">
                        <Button variant="ghost" className="w-full justify-start gap-2" data-track-cta>
                          <MessageSquare className="h-4 w-4" /> Contact
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <a href="https://plonbox.com" target="_blank" rel="noreferrer" className="w-full">
                        <Button variant="ghost" className="w-full justify-start gap-2" data-track-cta>
                          <ExternalLink className="h-4 w-4" /> Goal Planner
                        </Button>
                      </a>
                    </SheetClose>
                  </CollapsibleContent>
                </Collapsible>

                {/* Admin tools section */}
                <Collapsible open={mobileAdminOpen} onOpenChange={setMobileAdminOpen}>
                  <CollapsibleTrigger asChild>
                    <Button type="button" variant="secondary" className="w-full justify-between">
                      <span className="flex items-center gap-2">
                        <Activity className="h-4 w-4" /> Admin
                        {!isAdmin && <Lock className="h-4 w-4 opacity-70" />}
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileAdminOpen ? "rotate-180" : ""}`} />
                    </Button>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="mt-2 ml-4 grid gap-1">
                    {isAdmin ? (
                      <>
                        {errorCount > 0 && (
                          <SheetClose asChild>
                            <Link to="/admin/error-log" className="w-full">
                              <Button variant="ghost" className="w-full justify-start gap-2 text-destructive">
                                <AlertTriangle className="h-4 w-4" /> 
                                {errorCount} Error{errorCount !== 1 ? "s" : ""}
                              </Button>
                            </Link>
                          </SheetClose>
                        )}
                        <SheetClose asChild>
                          <Link to="/admin" className="w-full">
                            <Button variant="ghost" className="w-full justify-start">Admin hub</Button>
                          </Link>
                        </SheetClose>
                        
                        {/* Manage Contents group */}
                        <Collapsible open={mobileManageContentsOpen} onOpenChange={setMobileManageContentsOpen}>
                          <CollapsibleTrigger asChild>
                            <Button type="button" variant="ghost" className="w-full justify-between text-muted-foreground">
                              <span className="flex items-center gap-2">
                                <Settings className="h-4 w-4" /> Manage Contents
                              </span>
                              <ChevronDown className={`h-4 w-4 transition-transform ${mobileManageContentsOpen ? "rotate-180" : ""}`} />
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="ml-4 grid gap-1">
                            <SheetClose asChild>
                              <Link to="/course-manager" className="w-full">
                                <Button variant="ghost" className="w-full justify-start gap-2">
                                  <BookOpen className="h-4 w-4" /> Courses & Chapters
                                </Button>
                              </Link>
                            </SheetClose>
                            <SheetClose asChild>
                              <Link to="/quiz-learn-manager" className="w-full">
                                <Button variant="ghost" className="w-full justify-start gap-2">
                                  <Target className="h-4 w-4" /> Quiz & Learn
                                </Button>
                              </Link>
                            </SheetClose>
                            <SheetClose asChild>
                              <Link to="/admin/endless-quiz" className="w-full">
                                <Button variant="ghost" className="w-full justify-start gap-2">
                                  <Zap className="h-4 w-4" /> Quick Fire
                                </Button>
                              </Link>
                            </SheetClose>
                          </CollapsibleContent>
                        </Collapsible>
                        
                        <SheetClose asChild>
                          <Link to="/admin/analytics" className="w-full">
                            <Button variant="ghost" className="w-full justify-start gap-2">
                              <BarChart3 className="h-4 w-4" /> Admin Analytics
                            </Button>
                          </Link>
                        </SheetClose>
                        <SheetClose asChild>
                          <Link to="/admin/users" className="w-full">
                            <Button variant="ghost" className="w-full justify-start gap-2">
                              <Users className="h-4 w-4" /> Users & access
                            </Button>
                          </Link>
                        </SheetClose>
                      </>
                    ) : (
                      <>
                        {["Admin hub", "Manage content", "Admin Analytics", "Users & access"].map((label) => (
                          <Button
                            key={label}
                            type="button"
                            variant="ghost"
                            className="w-full justify-between"
                            onClick={() =>
                              toast({
                                title: "Admin only",
                                description: adminLockedMsg,
                              })
                            }
                          >
                            <span>{label}</span>
                            <Lock className="h-4 w-4 opacity-70" />
                          </Button>
                        ))}
                        <div className="px-1 pt-1 text-xs text-muted-foreground">
                          Requires admin login.
                        </div>
                      </>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Auth actions */}
                <div className="pt-3 border-t mt-2">
                  {user ? (
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => signOut()}
                      data-track-cta
                    >
                      <LogOut className="h-4 w-4" /> Sign out
                    </Button>
                  ) : (
                    <SheetClose asChild>
                      <Link to="/login" className="w-full">
                        <Button variant="secondary" className="w-full justify-start" data-track-cta>
                          Sign In
                        </Button>
                      </Link>
                    </SheetClose>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop - streamlined for laptop view */}
        <nav className="hidden sm:flex items-center gap-1">
          {/* Primary navigation links - always visible */}
          <Link to="/browse">
            <Button variant="ghost" size="sm" className="gap-1.5 px-2.5" data-track-cta>
              <BookOpen className="h-4 w-4" />
              <span className="hidden md:inline">Learn</span>
            </Button>
          </Link>

          <Link to="/quiz-learn">
            <Button variant="ghost" size="sm" className="gap-1.5 px-2.5" data-track-cta>
              <Target className="h-4 w-4" />
              <span className="hidden lg:inline">Q&L</span>
            </Button>
          </Link>

          {/* Progress - clean locked state */}
          {user ? (
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="gap-1.5 px-2.5" data-track-cta>
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden xl:inline">Progress</span>
              </Button>
            </Link>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 px-2.5 text-muted-foreground"
                  data-track-cta
                  onClick={() => {
                    toast({
                      title: "Sign in required",
                      description: "Track your learning progress after signing in.",
                    });
                  }}
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span className="hidden xl:inline">Progress</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Sign in to track progress</TooltipContent>
            </Tooltip>
          )}

          {/* More dropdown - consolidates secondary pages */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5 px-2.5" data-track-cta>
                <MoreHorizontal className="h-4 w-4" />
                <span className="hidden lg:inline">More</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 bg-popover z-50">
              <DropdownMenuItem asChild>
                <Link to="/analytics" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" /> Analytics
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/about" className="flex items-center gap-2">
                  <Info className="h-4 w-4" /> About
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/blog" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Blog
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/contact" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> Contact
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/products" className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> Products
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="https://plonbox.com" target="_blank" rel="noreferrer" className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" /> Goal Planner
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Divider before admin/actions */}
          <div className="h-4 w-px bg-border mx-1" />

          {/* Admin dropdown - cleaner grouped layout */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className={`gap-1.5 px-2.5 ${!isAdmin ? "text-muted-foreground" : ""}`} 
                data-track-cta
              >
                <Activity className="h-4 w-4" />
                <span className="hidden lg:inline">Admin</span>
                {!isAdmin && <Lock className="h-3 w-3 opacity-60" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-popover z-50">
              {isAdmin ? (
                <>
                  {/* Quick access */}
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center gap-2 font-medium">
                      <Settings className="h-4 w-4" /> Admin Hub
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  
                  {/* Content management group */}
                  <div className="px-2 py-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Content</span>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/course-manager" className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" /> Courses & Chapters
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/quiz-learn-manager" className="flex items-center gap-2">
                      <Target className="h-4 w-4" /> Quiz & Learn Sets
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/endless-quiz" className="flex items-center gap-2">
                      <Zap className="h-4 w-4" /> Quick Fire Questions
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  
                  {/* Analytics & management group */}
                  <div className="px-2 py-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Insights</span>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/analytics" className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" /> Analytics Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/users" className="flex items-center gap-2">
                      <Users className="h-4 w-4" /> Users & Access
                    </Link>
                  </DropdownMenuItem>
                </>
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  <Lock className="h-4 w-4 inline mr-2" />
                  {user ? "Admin access required" : "Sign in to request access"}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Action buttons - compact group */}
          <div className="flex items-center">
            {/* Backlog button (admin only) */}
            <BacklogButton />

            {/* Unified notification dropdown for all users */}
            <NotificationDropdown 
              quizLearnSets={homeData?.quizLearnSets}
              courses={homeData?.courses}
            />
          </div>

          {/* Auth button */}
          {user ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2.5"
                  onClick={() => signOut()}
                  data-track-cta
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Sign out</TooltipContent>
            </Tooltip>
          ) : (
            <Link to="/login">
              <Button variant="default" size="sm" className="px-3" data-track-cta>
                Sign in
              </Button>
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
});
